import { Button, Grid2 as Grid, LinearProgress, Paper, Tooltip } from "@mui/material"
import React, { ReactNode, useEffect, useRef, useState } from "react"
import { useDebouncer } from "../../hooks/useDebouncer"
import { Validator, ValidatorModel } from "../../validation"
import { useAppErrorStore } from "../../zstore/AppErrorStore"
import { NexAlert } from "../Alert/Alert"
import { ErrorBoundary } from "../ErrorBoundary"
import { LoadingButton } from "../LoadingButton/LoadingButton"
import { NexPageContainer } from "../NexPageContainer"
import { NexStepper } from "../Stepper/Stepper"
import { Body } from "../Typography/Typography"
import { useStyles } from "./styles"

export interface Step {
    label: string
    key: string
}

export type StepperValidatorProps = { dependencies: React.DependencyList; model: ValidatorModel }

interface StepperFormProps {
    activeStep?: number
    steps?: Step[]
    disableSubmit?: boolean
    empty?: boolean
    emptyText?: string
    errorMessages?: (string | undefined)[]
    header: ReactNode
    loading?: boolean
    onErrorClose?: () => void
    onStepChange: (newStep: number) => Promise<void> | void
    secondaryAction?: ReactNode
    submitting: boolean
    submitTooltip?: string
    validationProps?: StepperValidatorProps
    warnings?: string[]
    children: ReactNode
}

export const StepperForm = ({
    activeStep = 0,
    children,
    disableSubmit = false,
    empty,
    emptyText,
    errorMessages = [],
    header,
    loading,
    onErrorClose,
    onStepChange,
    secondaryAction,
    steps = [],
    submitting,
    submitTooltip,
    validationProps,
    warnings = [],
}: StepperFormProps) => {
    const { classes, cx } = useStyles()
    const appError = useAppErrorStore((state) => state.appError)
    const clearError = useAppErrorStore((state) => state.clear)

    const [completedSteps, setCompletedSteps] = useState<boolean[]>(Array(steps.length).fill(false))
    const stepLabels = steps.map((step: { label: string; key: string }) => step.label)
    const stepGroups = steps.length > 0 ? steps.map((step: { label: string; key: string }) => step.key) : null
    const isSingleStep = (steps && steps.length === 0) || false
    const isFinalStep = isSingleStep || activeStep === steps.length - 1
    const isValidating = Object.keys(validationProps ?? {}).length > 0
    const validErrorMessages = errorMessages.filter((err) => err)

    const dependencies = validationProps?.dependencies
    const model = validationProps?.model

    const scrollRef = useRef<HTMLDivElement | null>(null)

    // validation
    const getValidator = () => {
        return isValidating && model ? new Validator(model) : null
    }
    const [validator, setValidator] = useState<Validator | null>(getValidator())
    const isValid = validator && Object.keys(validator.validatorErrors).length === 0

    useEffect(() => {
        if (!loading) {
            if (isValidating) {
                const newValidator = getValidator()!
                newValidator.validate(stepGroups)
                if (stepGroups) {
                    handleCompletedSteps(newValidator)
                }
                setValidator(newValidator)
            }
        }
    }, [loading])

    const handleValidation = useDebouncer((model: ValidatorModel, stepGroups: string[]) => {
        const newValidator = new Validator(model)
        newValidator.validate(stepGroups)

        if (stepGroups) {
            handleCompletedSteps(newValidator)
        }
        setValidator(newValidator)
    }, 170)

    useEffect(() => {
        if (!loading && isValidating) {
            handleValidation(model, stepGroups)
        }
    }, dependencies)

    const handleCompletedSteps = (validator: Validator) => {
        if (stepGroups) {
            setCompletedSteps(stepGroups.map((grp: string) => validator.isGroupValid(grp)))
        }
    }

    const getValidatorErrors = (): ReactNode[] => {
        const result = []
        if (!validator) {
            return []
        }

        for (const key in validator?.validatorErrors) {
            const validatorError = validator?.validatorErrors[key]
            const { errorMessage: validatorErrorMessage, displayMessage } = validatorError!
            let message: ReactNode = validatorErrorMessage

            if (displayMessage) {
                const splitMessage = displayMessage.split(":")
                message = (
                    <>
                        <strong>{splitMessage[0]}:</strong>
                        {splitMessage[1]}
                    </>
                )
            }

            result.push(
                <Grid key={key}>
                    <li>{message}</li>
                </Grid>,
            )
        }

        return result
    }

    // general errors
    useEffect(() => {
        if ((validErrorMessages.length || appError) && scrollRef.current != null) {
            const top = scrollRef.current.offsetTop - 67
            window.scrollTo({ top, behavior: "smooth" })
        }
    }, [...validErrorMessages, appError, scrollRef.current])

    const handleErrorClose = () => {
        clearError()
        if (onErrorClose) {
            onErrorClose()
        }
    }

    // steps
    const handleContinue = () => {
        onStepChange(activeStep + 1)
    }

    const handleBack = () => {
        onStepChange(activeStep - 1)
    }

    return (
        <ErrorBoundary>
            <NexPageContainer py={4}>
                {header}
                <Paper className={classes.editorRoot} ref={scrollRef}>
                    {!isSingleStep && (
                        <Grid className={classes.gutterBottom__md}>
                            <NexStepper
                                steps={stepLabels}
                                completedSteps={loading ? [] : completedSteps}
                                activeStep={activeStep}
                                onClick={onStepChange}
                                disabled={loading}
                                className={cx({ [classes.stepper__narrow]: steps.length === 2 })}
                            />
                        </Grid>
                    )}

                    {(validErrorMessages.length !== 0 || appError) && (
                        <NexAlert animate onClose={handleErrorClose}>
                            {appError}
                            {validErrorMessages.map((err) => (
                                <li key={err}>{err}</li>
                            ))}
                        </NexAlert>
                    )}

                    {warnings.map((warning: string, idx: number) => (
                        <NexAlert key={idx} severity="warning">
                            {warning}
                        </NexAlert>
                    ))}

                    {loading ? (
                        <LinearProgress sx={{ mb: 2, marginTop: isSingleStep ? 2 : 0 }} />
                    ) : (
                        <>
                            {empty ? (
                                <Body className={cx(classes.empty_text, classes.gutterBottom__md)}>
                                    {emptyText ?? "Content failed to load."}
                                </Body>
                            ) : (
                                <>
                                    <Grid className={classes.gutterBottom__md}>{children}</Grid>

                                    {isValidating && isFinalStep && !isValid && (
                                        <NexAlert
                                            title="Please fix the following input validation errors:"
                                            gutterBottom="lg"
                                            className={classes.validation__alert}
                                        >
                                            {getValidatorErrors()}
                                        </NexAlert>
                                    )}
                                </>
                            )}
                            <Grid container spacing={2} justifyContent="space-between">
                                <Grid>
                                    <Button onClick={handleBack} variant="outlined" color="primary" size="large">
                                        Back
                                    </Button>
                                </Grid>
                                <Grid sx={{ display: "flex", gap: 2 }}>
                                    {secondaryAction}
                                    <Tooltip title={(isFinalStep && submitTooltip) || ""} arrow enterDelay={300}>
                                        <div>
                                            <LoadingButton
                                                onClick={handleContinue}
                                                pending={submitting}
                                                variant="contained"
                                                color="primary"
                                                size="large"
                                                disabled={
                                                    (isFinalStep && (isValidating ? !isValid : false)) ||
                                                    (disableSubmit && isFinalStep) ||
                                                    empty
                                                }
                                            >
                                                {isFinalStep ? "Submit" : "Continue"}
                                            </LoadingButton>
                                        </div>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Paper>
            </NexPageContainer>
        </ErrorBoundary>
    )
}
